import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, RadialBarChart, RadialBar, PolarAngleAxis,
} from "recharts";
import {
  LogOut, TrendingUp, BookOpen, Flame, Coins, Star, Target, ArrowUpRight, ArrowDownRight, Lightbulb, UserPlus, Plus,
  Users, FileDown, CheckCircle2, XCircle, ClipboardList,
} from "lucide-react";
import { toast } from "sonner";
import api, { apiErr } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { Logo, Avatar } from "../../components/common";
import { AVATARS, DIFFICULTIES, EXERCISE_TYPES, MATH_EXERCISE_TYPES, SUBJECTS } from "../../lib/constants";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from "../../components/ui/dialog";

export default function ParentDashboard() {
  const navigate = useNavigate();
  const { logout, user } = useAuth();
  const [children, setChildren] = useState([]);
  const [selected, setSelected] = useState(null);
  const [progress, setProgress] = useState(null);
  const [linkEmail, setLinkEmail] = useState("");
  const [childName, setChildName] = useState("");
  const [saving, setSaving] = useState(false);
  const [open, setOpen] = useState(false);
  const [subjectTab, setSubjectTab] = useState("lezen");
  const [attempts, setAttempts] = useState([]);
  const [reviewData, setReviewData] = useState(null);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const loadChildren = async () => {
    const { data } = await api.get("/parent/children");
    setChildren(data.children);
    if (data.children.length && !selected) setSelected(data.children[0].id);
  };
  useEffect(() => { loadChildren(); }, []);
  useEffect(() => {
    if (!selected) return;
    api.get(`/parent/child/${selected}/progress`).then(({ data }) => setProgress(data));
    api.get(`/parent/child/${selected}/attempts`).then(({ data }) => setAttempts(data.attempts));
  }, [selected]);

  const openReview = async (attemptId) => {
    setReviewOpen(true);
    setReviewData(null);
    try {
      const { data } = await api.get(`/parent/child/${selected}/attempt/${attemptId}`);
      setReviewData(data);
    } catch (err) { toast.error(apiErr(err.response?.data?.detail)); setReviewOpen(false); }
  };

  const downloadPdf = async (subject) => {
    setDownloading(true);
    try {
      const { data, headers } = await api.get(`/parent/child/${selected}/report.pdf?subject=${subject}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([data], { type: "application/pdf" }));
      const child = progress?.child?.name || "kind";
      const a = document.createElement("a");
      a.href = url;
      a.download = `SlimAvontuur-${child}-${SUBJECTS[subject].label}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("PDF gedownload 📄");
    } catch (err) { toast.error("PDF maken mislukt"); }
    finally { setDownloading(false); }
  };

  const fmtDate = (iso) => {
    try { return new Date(iso).toLocaleDateString("nl-NL", { day: "numeric", month: "short", year: "numeric" }); }
    catch { return (iso || "").slice(0, 10); }
  };

  const addChild = async () => {
    if (!linkEmail.trim()) { toast.error("Vul het e-mailadres van je kind in"); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/parent/create-child", { name: childName, email: linkEmail });
      toast.success(data.message || "Kind toegevoegd!");
      setLinkEmail(""); setChildName(""); setOpen(false);
      await loadChildren();
    } catch (err) { toast.error(apiErr(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div className="min-h-screen bg-background pb-16">
      <header className="bg-white sticky top-0 z-30 border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-5 h-16 flex items-center justify-between">
          <Logo />
          <div className="flex items-center gap-3">
            <span className="hidden sm:block text-sm font-semibold text-slate-500">{user?.name}</span>
            {user?.role === "admin" && (
              <Button variant="outline" onClick={() => navigate("/admin")} className="rounded-full font-bold" data-testid="parent-to-admin-btn">← Admin</Button>
            )}
            <Button variant="ghost" size="icon" onClick={() => { logout(); navigate("/"); }} className="rounded-full" data-testid="parent-logout-btn"><LogOut className="w-5 h-5" /></Button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-5 pt-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="font-heading text-3xl font-extrabold text-slate-900">Ouderdashboard</h1>
            <p className="text-slate-500 font-medium">Volg de voortgang van je kind(eren)</p>
          </div>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="rounded-full font-bold bg-primary hover:bg-blue-600" data-testid="link-child-btn"><UserPlus className="w-4 h-4 mr-1.5" /> Kind toevoegen</Button>
            </DialogTrigger>
            <DialogContent className="rounded-3xl">
              <DialogHeader><DialogTitle className="font-heading">Kind toevoegen</DialogTitle>
                <DialogDescription>Maak een account voor je kind aan. Je kind logt later in met dit e‑mailadres via een magische link. Bestaat het account al, dan koppelen we het aan jou.</DialogDescription>
              </DialogHeader>
              <div className="space-y-3">
                <Input value={childName} onChange={(e) => setChildName(e.target.value)} placeholder="Naam van je kind (bijv. Lotte)" className="h-12 rounded-xl border-2" data-testid="child-name-input" />
                <Input value={linkEmail} onChange={(e) => setLinkEmail(e.target.value)} placeholder="kind@email.nl" type="email" className="h-12 rounded-xl border-2" data-testid="link-email-input" />
              </div>
              <DialogFooter><Button onClick={addChild} disabled={saving} className="rounded-full font-bold bg-accent hover:bg-orange-600" data-testid="confirm-link-btn">{saving ? "Bezig..." : "Toevoegen"}</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        </div>

        {/* Child selector */}
        <div className="flex gap-3 mb-8 flex-wrap">
          {children.map((c) => (
            <button key={c.id} onClick={() => setSelected(c.id)} data-testid={`child-${c.id}`}
              className={`flex items-center gap-3 px-4 py-3 rounded-2xl border-2 transition-all ${selected === c.id ? "bg-white border-primary shadow" : "bg-white border-slate-200 hover:border-slate-300"}`}>
              <Avatar emoji={AVATARS[c.avatar] || "🦊"} size="sm" ring={false} />
              <div className="text-left"><p className="font-bold text-slate-900">{c.name}</p><p className="text-xs text-slate-400 font-semibold">Level {c.level.level} · Lezen {DIFFICULTIES[c.levels?.lezen] || "—"} · Rekenen {DIFFICULTIES[c.levels?.rekenen] || "—"}</p></div>
            </button>
          ))}
          {children.length === 0 && (
            <div className="w-full bg-white rounded-2xl border-2 border-dashed border-slate-200 p-10 text-center">
              <p className="text-slate-500 font-semibold">Nog geen kind toegevoegd. Klik op "Kind toevoegen" om te beginnen.</p>
            </div>
          )}
        </div>

        {progress && (
          <>
            {/* Stat cards */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              {[
                { label: "Gem. score", value: `${progress.avg_score}%`, icon: Target, color: "text-primary bg-blue-50" },
                { label: "Oefeningen", value: progress.total_exercises, icon: BookOpen, color: "text-green-600 bg-green-50" },
                { label: "Streak", value: `${progress.child.streak} dagen`, icon: Flame, color: "text-accent bg-orange-50" },
                { label: "Munten", value: progress.child.coins, icon: Coins, color: "text-amber-500 bg-amber-50" },
              ].map((s) => (
                <div key={s.label} className="bg-white rounded-2xl border border-slate-200 p-5" data-testid={`stat-${s.label}`}>
                  <div className={`w-11 h-11 rounded-xl ${s.color} flex items-center justify-center mb-3`}><s.icon className="w-6 h-6" /></div>
                  <p className="font-heading text-2xl font-black text-slate-900">{s.value}</p>
                  <p className="text-sm text-slate-400 font-semibold">{s.label}</p>
                </div>
              ))}
            </div>

            {/* Level radial + charts */}
            <div className="grid lg:grid-cols-3 gap-6 mb-6">
              <div className="bg-white rounded-2xl border border-slate-200 p-6 flex flex-col items-center justify-center">
                <p className="font-heading font-bold text-slate-800 self-start mb-2">Leesniveau</p>
                <ResponsiveContainer width="100%" height={170}>
                  <RadialBarChart innerRadius="70%" outerRadius="100%" data={[{ value: progress.child.level.pct, fill: "#3B82F6" }]} startAngle={90} endAngle={-270}>
                    <PolarAngleAxis type="number" domain={[0, 100]} tick={false} />
                    <RadialBar background dataKey="value" cornerRadius={20} />
                  </RadialBarChart>
                </ResponsiveContainer>
                <p className="font-heading text-xl font-black text-slate-900 -mt-4">Level {progress.child.level.level}</p>
                <p className="text-sm text-slate-400 font-semibold">{progress.child.level.name}</p>
              </div>

              <div className="bg-white rounded-2xl border border-slate-200 p-6 lg:col-span-2">
                <p className="font-heading font-bold text-slate-800 mb-4 flex items-center gap-2"><TrendingUp className="w-5 h-5 text-primary" /> XP deze week</p>
                <ResponsiveContainer width="100%" height={190}>
                  <BarChart data={progress.weekly}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E2E8F0" />
                    <XAxis dataKey="day" tickLine={false} axisLine={false} tick={{ fontSize: 12, fontWeight: 600, fill: "#94A3B8" }} />
                    <YAxis tickLine={false} axisLine={false} tick={{ fontSize: 12, fill: "#94A3B8" }} />
                    <Tooltip cursor={{ fill: "#F1F5F9" }} contentStyle={{ borderRadius: 12, border: "1px solid #E2E8F0" }} />
                    <Bar dataKey="xp" fill="#3B82F6" radius={[8, 8, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Subject tabs */}
            <div className="flex gap-2 mb-5">
              {Object.entries(SUBJECTS).map(([key, s]) => (
                <button key={key} onClick={() => setSubjectTab(key)} data-testid={`parent-subject-${key}`}
                  className={`px-5 py-2.5 rounded-full font-bold text-sm transition-all ${subjectTab === key ? "bg-primary text-white shadow" : "bg-white text-slate-500 border border-slate-200"}`}>
                  {s.emoji} {s.label}
                </button>
              ))}
            </div>

            {/* Peer comparison + PDF download */}
            {(() => {
              const sub = progress.subjects[subjectTab];
              const p = sub.peers || {};
              return (
                <div className="grid lg:grid-cols-3 gap-6 mb-6">
                  <div className="bg-white rounded-2xl border border-slate-200 p-6 lg:col-span-2" data-testid={`peers-${subjectTab}`}>
                    <p className="font-heading font-bold text-slate-800 mb-1 flex items-center gap-2"><Users className="w-5 h-5 text-primary" /> Vergelijking met leeftijdsgenoten</p>
                    <p className="text-sm text-slate-400 font-semibold mb-4">Anoniem gemiddelde op niveau {DIFFICULTIES[p.level] || p.level_label || p.level}</p>
                    {p.my_accuracy == null ? (
                      <p className="text-slate-400 font-medium text-sm">Nog geen oefeningen gemaakt voor dit vak.</p>
                    ) : (
                      <div className="space-y-4">
                        <div>
                          <div className="flex justify-between mb-1 text-sm font-bold"><span className="text-slate-700">{progress.child.name}</span><span className="text-primary">{p.my_accuracy}%</span></div>
                          <div className="h-3 rounded-full bg-slate-100 overflow-hidden"><div className="h-full rounded-full bg-primary" style={{ width: `${p.my_accuracy}%` }} /></div>
                        </div>
                        <div>
                          <div className="flex justify-between mb-1 text-sm font-bold"><span className="text-slate-500">Gemiddeld op dit niveau</span><span className="text-slate-500">{p.peer_average == null ? "—" : `${p.peer_average}%`}</span></div>
                          <div className="h-3 rounded-full bg-slate-100 overflow-hidden"><div className="h-full rounded-full bg-slate-300" style={{ width: `${p.peer_average || 0}%` }} /></div>
                        </div>
                        {p.peer_average == null ? (
                          <p className="text-xs text-slate-400 font-semibold">Nog te weinig kinderen op dit niveau om te vergelijken.</p>
                        ) : (
                          <p className="text-sm font-bold" data-testid={`peers-verdict-${subjectTab}`} style={{ color: p.my_accuracy >= p.peer_average ? "#16A34A" : "#F97316" }}>
                            {p.my_accuracy >= p.peer_average
                              ? `🎉 ${p.my_accuracy - p.peer_average}% boven het gemiddelde van ${p.peer_count} ${p.peer_count === 1 ? "kind" : "kinderen"}`
                              : `${p.peer_average - p.my_accuracy}% onder het gemiddelde van ${p.peer_count} ${p.peer_count === 1 ? "kind" : "kinderen"}`}
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="bg-white rounded-2xl border border-slate-200 p-6 flex flex-col justify-center">
                    <p className="font-heading font-bold text-slate-800 mb-1 flex items-center gap-2"><FileDown className="w-5 h-5 text-accent" /> Rapport delen</p>
                    <p className="text-sm text-slate-400 font-semibold mb-4">Download alle vragen en antwoorden voor {SUBJECTS[subjectTab].label} als PDF.</p>
                    <Button onClick={() => downloadPdf(subjectTab)} disabled={downloading} className="rounded-full font-bold bg-accent hover:bg-orange-600" data-testid={`download-pdf-${subjectTab}`}>
                      <FileDown className="w-4 h-4 mr-1.5" /> {downloading ? "Bezig..." : `PDF ${SUBJECTS[subjectTab].label}`}
                    </Button>
                  </div>
                </div>
              );
            })()}

            {/* Strong / weak skills */}
            <div className="grid lg:grid-cols-2 gap-6 mb-6">
              <div className="bg-white rounded-2xl border border-slate-200 p-6">
                <p className="font-heading font-bold text-slate-800 mb-4">Sterke vaardigheden</p>
                {progress.subjects[subjectTab].strong_skills.length ? progress.subjects[subjectTab].strong_skills.map((s) => (
                  <div key={s.skill} className="flex items-center justify-between py-2.5 border-b border-slate-100 last:border-0">
                    <span className="font-semibold text-slate-700">{s.label}</span>
                    <span className="flex items-center gap-1 font-bold text-green-600"><ArrowUpRight className="w-4 h-4" /> {s.accuracy}%</span>
                  </div>
                )) : <p className="text-slate-400 font-medium text-sm">Nog niet genoeg data.</p>}
              </div>
              <div className="bg-white rounded-2xl border border-slate-200 p-6">
                <p className="font-heading font-bold text-slate-800 mb-4">Aandachtspunten</p>
                {progress.subjects[subjectTab].weak_skills.length ? progress.subjects[subjectTab].weak_skills.map((s) => (
                  <div key={s.skill} className="flex items-center justify-between py-2.5 border-b border-slate-100 last:border-0">
                    <span className="font-semibold text-slate-700">{s.label}</span>
                    <span className="flex items-center gap-1 font-bold text-red-500"><ArrowDownRight className="w-4 h-4" /> {s.accuracy}%</span>
                  </div>
                )) : <p className="text-slate-400 font-medium text-sm">Nog niet genoeg data.</p>}
              </div>
            </div>

            {/* Recommendations */}
            <div className="bg-white rounded-2xl border border-slate-200 p-6 mb-6">
              <p className="font-heading font-bold text-slate-800 mb-4 flex items-center gap-2"><Lightbulb className="w-5 h-5 text-amber-500" /> Oefenadviezen · {SUBJECTS[subjectTab].label}</p>
              <div className="grid sm:grid-cols-3 gap-4">
                {progress.subjects[subjectTab].recommendations.length ? progress.subjects[subjectTab].recommendations.map((r, i) => (
                  <div key={i} className="p-4 rounded-2xl bg-secondary/40 border border-secondary" data-testid={`rec-${i}`}>
                    <p className="font-bold text-slate-900">{r.skill}</p>
                    <p className="text-sm text-slate-500 font-medium mt-1">{r.reason}</p>
                    <p className="text-xs font-bold text-primary mt-2">→ {EXERCISE_TYPES[r.type]?.label || MATH_EXERCISE_TYPES[r.type]?.label || r.type}</p>
                  </div>
                )) : <p className="text-slate-400 font-medium text-sm">Nog geen oefeningen gemaakt voor dit vak.</p>}
              </div>
            </div>

            {/* Monthly trend */}
            {progress.monthly.length > 1 && (
              <div className="bg-white rounded-2xl border border-slate-200 p-6">
                <p className="font-heading font-bold text-slate-800 mb-4">Maandelijkse trend</p>
                <ResponsiveContainer width="100%" height={200}>
                  <LineChart data={progress.monthly}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E2E8F0" />
                    <XAxis dataKey="month" tickLine={false} axisLine={false} tick={{ fontSize: 12, fill: "#94A3B8" }} />
                    <YAxis tickLine={false} axisLine={false} tick={{ fontSize: 12, fill: "#94A3B8" }} />
                    <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #E2E8F0" }} />
                    <Line type="monotone" dataKey="xp" stroke="#F97316" strokeWidth={3} dot={{ r: 4 }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}
            {/* Gemaakte oefeningen (vragen & antwoorden terugzien) */}
            <div className="bg-white rounded-2xl border border-slate-200 p-6 mt-6" data-testid="attempts-section">
              <p className="font-heading font-bold text-slate-800 mb-4 flex items-center gap-2"><ClipboardList className="w-5 h-5 text-primary" /> Gemaakte oefeningen</p>
              {attempts.length === 0 ? (
                <p className="text-slate-400 font-medium text-sm">Nog geen oefeningen gemaakt.</p>
              ) : (
                <div className="space-y-2">
                  {attempts.map((a) => (
                    <div key={a.id} className="flex items-center justify-between gap-3 p-3 rounded-xl border border-slate-100 hover:border-slate-200" data-testid={`attempt-${a.id}`}>
                      <div className="min-w-0">
                        <p className="font-bold text-slate-800 truncate">{a.title}</p>
                        <p className="text-xs text-slate-400 font-semibold">{fmtDate(a.created_at)} · {SUBJECTS[a.subject]?.label || a.subject}</p>
                      </div>
                      <div className="flex items-center gap-3 shrink-0">
                        <span className="font-heading font-black text-slate-900">{Math.round((a.score / a.total) * 100)}%</span>
                        <span className="text-xs font-semibold text-slate-400">{a.score}/{a.total}</span>
                        <Button variant="outline" size="sm" disabled={!a.has_details} onClick={() => openReview(a.id)} className="rounded-full font-bold" data-testid={`review-${a.id}`}>
                          {a.has_details ? "Bekijk" : "Geen details"}
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Review dialog: alle vragen en gegeven antwoorden */}
            <Dialog open={reviewOpen} onOpenChange={setReviewOpen}>
              <DialogContent className="rounded-3xl max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="review-dialog">
                <DialogHeader>
                  <DialogTitle className="font-heading">{reviewData?.title || "Oefening"}</DialogTitle>
                  <DialogDescription>
                    {reviewData ? `${fmtDate(reviewData.created_at)} · ${SUBJECTS[reviewData.subject]?.label || reviewData.subject} · score ${reviewData.score}/${reviewData.total}` : "Laden..."}
                  </DialogDescription>
                </DialogHeader>
                {reviewData && (
                  <div className="space-y-4">
                    {reviewData.results.map((r, i) => (
                      <div key={i} className="p-4 rounded-2xl border border-slate-200" data-testid={`review-q-${i}`}>
                        <div className="flex items-start gap-2 mb-2">
                          {r.is_correct ? <CheckCircle2 className="w-5 h-5 text-green-600 shrink-0 mt-0.5" /> : <XCircle className="w-5 h-5 text-red-500 shrink-0 mt-0.5" />}
                          <p className="font-bold text-slate-900">{i + 1}. {r.question}</p>
                        </div>
                        <div className="pl-7 space-y-1 text-sm">
                          <p className={r.is_correct ? "text-green-700 font-semibold" : "text-red-600 font-semibold"}>Antwoord: {r.your_answer || "(geen antwoord)"}</p>
                          {!r.is_correct && <p className="text-slate-600 font-semibold">Juiste antwoord: {r.correct_answer}</p>}
                          {r.skill_label && <p className="text-xs text-slate-400 font-bold uppercase tracking-wide">{r.skill_label}</p>}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </DialogContent>
            </Dialog>
          </>
        )}
      </main>
    </div>
  );
}
